// src/components/DirectorUserManagement.js

import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api/axiosConfig";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { FiSearch, FiArrowLeft, FiClock } from "react-icons/fi";
import "./BranchManagerUserManagement.css";

const formatDateForInput = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatNotificationDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const getMetric = (member, key) => {
  return member[key] || 0;
};

const DirectorUserManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [allUsers, setAllUsers] = useState([]);
  const [selectedZonalHead, setSelectedZonalHead] = useState(null);
  const [selectedBranchManager, setSelectedBranchManager] = useState(null);
  const [selectedTeamLead, setSelectedTeamLead] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [directBranchManagers, setDirectBranchManagers] = useState([]);
  const [directTeamLeads, setDirectTeamLeads] = useState([]);
  const [directRMs, setDirectRMs] = useState([]);
  const [isDirectOverviewLoading, setIsDirectOverviewLoading] = useState(false);
  const [rmModalOpen, setRmModalOpen] = useState(false);
  const [rmModalTitle, setRmModalTitle] = useState("");
  const [rmModalData, setRmModalData] = useState([]);
  const [isRmModalLoading, setIsRmModalLoading] = useState(false);
  const [rmModalMetrics, setRmModalMetrics] = useState([]);
  const [bmModalOpen, setBmModalOpen] = useState(false);
  const [bmModalTitle, setBmModalTitle] = useState("");
  const [bmModalData, setBmModalData] = useState([]);
  const [bmModalMode, setBmModalMode] = useState("team_leads"); // team_leads | rms
  const [isBmModalLoading, setIsBmModalLoading] = useState(false);
  const [bmModalKey, setBmModalKey] = useState(null);
  const [bmBranchId, setBmBranchId] = useState(null);
  const [bmSelectedTeamLead, setBmSelectedTeamLead] = useState(null);
  const [zonalDetailHead, setZonalDetailHead] = useState(null);
  const [zonalDetailData, setZonalDetailData] = useState({
    branchManagers: [],
    teamLeads: [],
    relationshipManagers: [],
  });
  const [isZonalDetailLoading, setIsZonalDetailLoading] = useState(false);
  const [isZonalDialogOpen, setIsZonalDialogOpen] = useState(false);

  const [dateRange, setDateRange] = useState({
    startDate: (() => {
      const d = new Date();
      d.setDate(d.getDate() - 29);
      return d;
    })(),
    endDate: new Date(),
  });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      let response;
      if (selectedTeamLead) {
        // Fetch RMs under the selected team lead
        response = await api.get(`/api/users/rms-by-team-lead/${selectedTeamLead.userId}`);
      } else if (selectedBranchManager) {
        // Fetch team leads under the selected branch manager
        response = await api.get("/api/users/team-leads-by-branch-manager", {
          params: {
            branchManagerId: selectedBranchManager.userId,
            range: 30,
          },
        });
      } else if (selectedZonalHead) {
        // Fetch branch managers under the selected zonal head
        response = await api.get("/api/users/branch-managers-by-zonal-head", {
          params: {
            zonalHeadId: selectedZonalHead.userId,
            startDate: formatDateForInput(dateRange.startDate),
            endDate: formatDateForInput(dateRange.endDate),
          },
        });
      } else {
        // Fetch zonal heads under director
        response = await api.get("/api/users/zonal-heads-by-director", {
          params: {
            startDate: formatDateForInput(dateRange.startDate),
            endDate: formatDateForInput(dateRange.endDate),
          },
        });
      }
      const users = normalizeList(response.data || []);
      setAllUsers(users);

      // Check for navigation state *after* the data has been fetched.
      if (location.state?.preSelectedZonalHead && !selectedZonalHead) {
        const preSelected = users.find(
          (user) => user.userId === location.state.preSelectedZonalHead.userId
        );
        if (preSelected) {
          setSelectedZonalHead(preSelected);
        }
        // Clear the state to prevent it from re-triggering on refresh
        navigate(location.pathname, { replace: true, state: {} });
      }
    } catch (err) {
      setError("Failed to fetch data. Please try again later.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, location, navigate, selectedZonalHead, selectedBranchManager, selectedTeamLead]);

  useEffect(() => {
    // Reset selections to null on component mount to ensure initial view shows zonal heads
    setSelectedZonalHead(null);
    setSelectedBranchManager(null);
    setSelectedTeamLead(null);
    fetchData();
  }, [fetchData]);

  // Filter users based on current view
  const getCurrentRole = () => {
    if (selectedTeamLead) return "relationship_manager";
    if (selectedBranchManager) return "team_lead";
    if (selectedZonalHead) return "branch_manager";
    return "zonal_head";
  };

  const filteredUsers = allUsers.filter(
    (u) =>
      u.role === getCurrentRole() &&
      (u.name || u.username).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleBack = () => {
    if (selectedTeamLead) {
      setSelectedTeamLead(null);
    } else if (selectedBranchManager) {
      setSelectedBranchManager(null);
    } else if (selectedZonalHead) {
      setSelectedZonalHead(null);
    }
    setSearchTerm("");
  };

  const handleZonalHeadClick = (zh) => {
    if (!zh) return;
    setZonalDetailHead(zh);
    setZonalDetailData({
      branchManagers: [],
      teamLeads: [],
      relationshipManagers: [],
    });
    setIsZonalDialogOpen(true);
    setSearchTerm("");
  };

  const handleBranchManagerClick = (bm) => {
    setSelectedBranchManager(bm);
    setSearchTerm("");
  };

  const handleTeamLeadClick = (tl) => {
    setSelectedTeamLead(tl);
    setSearchTerm("");
  };

  const closeZonalDialog = () => {
    setIsZonalDialogOpen(false);
    setZonalDetailHead(null);
    setZonalDetailData({
      branchManagers: [],
      teamLeads: [],
      relationshipManagers: [],
    });
  };

  const normalizeList = (arr = []) =>
    (arr || []).map((u) => ({
      ...u,
      userId: u.userId || u.user_id || u.id,
    }));

  const getCurrentTitle = () => {
    if (selectedTeamLead) {
      return `Relationship Managers under ${selectedTeamLead.name}`;
    } else if (selectedBranchManager) {
      return `Team Leads under ${selectedBranchManager.name}`;
    } else if (selectedZonalHead) {
      return `Branch Managers under ${selectedZonalHead.name}`;
    }
    return "Zonal Head Overview";
  };

  const getCurrentSearchPlaceholder = () => {
    if (selectedTeamLead) return "Relationship Manager";
    if (selectedBranchManager) return "Team Lead";
    if (selectedZonalHead) return "Branch Manager";
    return "Zonal Head";
  };

  const fetchDirectOverviews = useCallback(async () => {
    setIsDirectOverviewLoading(true);
    try {
      const [bmRes, tlRes, rmRes] = await Promise.all([
        api.get("/api/users/branch-managers-by-director"),
        api.get("/api/users/team-leads-by-director"),
        api.get("/api/users/relationship-managers-by-director"),
      ]);
      const normalize = (arr = []) =>
        (arr || []).map((u) => ({
          ...u,
          userId: u.userId || u.user_id || u.id,
          branchId: u.branchId || u.branch_id || (u.role === "branch_manager" ? u.id : null),
        }));
      setDirectBranchManagers(normalize(bmRes.data || []));
      setDirectTeamLeads(normalize(tlRes.data || []));
      setDirectRMs(
        normalize(rmRes.data || []).filter(
          (rm) =>
            (rm.reportsToRole ||
              rm.reports_to_role ||
              rm.reportsTo ||
              rm.role ||
              "") === "director"
        )
      );
    } catch (err) {
      console.error("Failed to fetch direct overviews", err);
    } finally {
      setIsDirectOverviewLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDirectOverviews();
  }, [fetchDirectOverviews]);

  const fetchZonalDetail = useCallback(async () => {
    if (!zonalDetailHead) return;
    setIsZonalDetailLoading(true);
    try {
      const zid =
        zonalDetailHead.userId ||
        zonalDetailHead.id ||
        zonalDetailHead.zonalId ||
        zonalDetailHead.zonal_id;
      const zonalParams = {
        zonalHeadId: zid,
        zonalId: zid,
        startDate: formatDateForInput(dateRange.startDate),
        endDate: formatDateForInput(dateRange.endDate),
      };
      const queryParams = {
        params: zonalParams,
      };
      const [bmRes, tlRes, rmRes] = await Promise.all([
        api.get("/api/users/branch-managers-by-zonal-head", queryParams),
        api.get("/api/users/team-leads-by-zonal-head", queryParams),
        api.get("/api/users/relationship-managers-by-zonal-head", queryParams),
      ]);
      setZonalDetailData({
        branchManagers: normalizeList(bmRes.data || []),
        teamLeads: normalizeList(tlRes.data || []),
        relationshipManagers: normalizeList(rmRes.data || []),
      });
    } catch (err) {
      console.error("Failed to load zonal detail data", err);
      setZonalDetailData({
        branchManagers: [],
        teamLeads: [],
        relationshipManagers: [],
      });
    } finally {
      setIsZonalDetailLoading(false);
    }
  }, [zonalDetailHead, dateRange]);

  useEffect(() => {
    fetchZonalDetail();
  }, [fetchZonalDetail]);

  const loadBmModalData = useCallback(async () => {
    if (!bmModalOpen || !bmModalKey) return;
    setIsBmModalLoading(true);
    try {
      const derivedBranch =
        bmBranchId ??
        (directBranchManagers.find(
          (bm) => Number(bm.userId) === Number(bmModalKey)
        )?.branchId || null);

      if (bmModalMode === "team_leads") {
        const targetBranch = derivedBranch || bmModalKey;
        const tlRes = await api.get(
          `/api/users/team-leads-by-director?branchManagerId=${targetBranch}`
        );
        // Backend already filters correctly, no need to filter again
        setBmModalData(tlRes.data || []);
      } else {
        const rmRes = await api.get(
          `/api/users/relationship-managers-by-director?branchManagerId=${bmModalKey}`
        );
        const rows = rmRes.data || [];
        const filtered = rows.filter((rm) => {
          const role =
            rm.reportsToRole ||
            rm.reports_to_role ||
            rm.reportsToRole ||
            "";
          const id =
            rm.reportsToId ||
            rm.reports_to_id ||
            rm.reportsTo ||
            rm.reports_to_id ||
            null;
          if (derivedBranch !== null) {
            return (
              role === "branch_manager" &&
              id !== null &&
              Number(id) === Number(derivedBranch)
            );
          }
          return role === "branch_manager" && id !== null;
        });
        setBmModalData(filtered);
      }
    } catch (err) {
      console.error("Failed to load branch manager drill data", err);
    } finally {
      setIsBmModalLoading(false);
    }
  }, [bmModalOpen, bmModalKey, bmModalMode, bmBranchId]);

  useEffect(() => {
    loadBmModalData();
  }, [loadBmModalData, bmModalMode]);

  const metricColumns = [
    { key: "telephoneSalesCalls", label: "Calls" },
    { key: "faceToFace", label: "F2F" },
    { key: "appointmentMade", label: "Appt. Made" },
    { key: "appointmentKept", label: "Appt. Kept" },
    { key: "factFind", label: "Fact Find" },
    { key: "serviceReviews", label: "Reviews" },
    { key: "socialCalls", label: "Social" },
    { key: "highIncomeWealthy", label: "HIW" },
    { key: "referrals", label: "Referrals" },
    { key: "closingInterview", label: "Closing Int." },
    { key: "salesClose", label: "Sales Close" },
  ];

  const renderDirectTable = (title, data, nameLabel) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography variant="h6" sx={{ mt: 1 }}>
        {title}
      </Typography>
      <TableContainer component={Paper} sx={{ mt: 1 }} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>{nameLabel}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Director</TableCell>
              {metricColumns.map((col) => (
                <TableCell key={col.key} align="right" sx={{ fontWeight: 700 }}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(data || [])
              // If this is RM overview, keep only actual RMs (prevent zonal heads or others)
              .filter((user) =>
                title.toLowerCase().includes("relationship")
                  ? (() => {
                      const role = (user.role || user.userRole || "").toLowerCase();
                      // Exclude known non-RM roles
                      if (
                        role.includes("zonal") ||
                        role.includes("branch") ||
                        role.includes("team_lead")
                      ) {
                        return false;
                      }
                      // Keep obvious RM roles, or anything that isn't clearly another leader type
                      return (
                        role.includes("relationship") ||
                        role === "" ||
                        role === "rm" ||
                        role === "relationship manager"
                      );
                    })()
                  : true
              )
              .map((user) => (
              <TableRow
                key={`${title}-${user.userId || user.id}`}
                hover
                sx={{
                  cursor:
                    title.toLowerCase().includes("team lead") ||
                    title.toLowerCase().includes("branch manager") ||
                    title.toLowerCase().includes("zonal head")
                      ? "pointer"
                      : "default",
                }}
                onClick={async () => {
                  const lowerTitle = title.toLowerCase();
                  if (lowerTitle.includes("zonal head")) {
                    handleZonalHeadClick(user);
                    return;
                  }
                  if (!lowerTitle.includes("team lead") && !lowerTitle.includes("branch manager")) return;
                  
                  try {
                    if (lowerTitle.includes("team lead")) {
                      // For team lead clicks, open RM modal
                      setIsRmModalLoading(true);
                      setRmModalTitle(`RMs under ${user.name || user.username || "Team Lead"}`);
                      setRmModalData([]);
                      setRmModalOpen(true);
                      
                      const teamLeadKey =
                        user.id ||
                        user.teamLeadId ||
                        user.team_lead_id ||
                        user.userId;
                      const res = await api.get(
                        `/api/users/relationship-managers-by-director?teamLeadId=${teamLeadKey}`
                      );
                      const rows = res.data || [];
                      const filteredRows = rows.filter((rm) => {
                        const role =
                          (rm.reportsToRole ||
                            rm.reports_to_role ||
                            rm.reportsToRole) || "";
                        const reportsId =
                          rm.reportsToId ||
                          rm.reports_to_id ||
                          rm.reportsTo ||
                          rm.teamLeadId ||
                          rm.team_lead_id;
                        return (
                          role === "team_lead" &&
                          reportsId !== undefined &&
                          Number(reportsId) === Number(teamLeadKey)
                        );
                      });
                      setRmModalData(filteredRows);
                      setIsRmModalLoading(false);
                    } else if (lowerTitle.includes("branch manager")) {
                      // For branch manager clicks, open BM modal with TL/RM buttons (NOT RM modal)
                      const bmKey = user.userId || user.id;
                      const branchId = user.branchId || user.branch_id || null;
                      setBmModalTitle(user.name || user.username || "Branch Manager");
                      setBmModalKey(bmKey);
                      setBmBranchId(branchId);
                      setBmModalMode("team_leads");
                      setBmModalData([]);
                      setBmModalOpen(true);
                      setIsBmModalLoading(true);
                      try {
                        const tlRes = await api.get(
                          `/api/users/team-leads-by-director?branchManagerId=${bmKey}`
                        );
                        setBmModalData(tlRes.data || []);
                      } finally {
                        setIsBmModalLoading(false);
                      }
                    }
                  } catch (err) {
                    console.error("Failed to load data", err);
                    setIsRmModalLoading(false);
                  }
                }}
              >
                <TableCell sx={{ fontWeight: 700, color: "#5b21b6" }}>
                  {user.name || user.username}
                </TableCell>
                <TableCell sx={{ color: user.leaveStatus ? "#b45309" : "#16A34A", fontWeight: 700 }}>
                  {user.leaveStatus ? "On Leave" : "Active"}
                </TableCell>
                <TableCell>{user.director || user.directorName || user.director_name || "Director"}</TableCell>
                {metricColumns.map((col) => (
                  <TableCell key={`${user.userId}-${col.key}`} align="right">
                    {getMetric(user, col.key)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {(data || []).length === 0 && (
              <TableRow>
                <TableCell colSpan={3 + metricColumns.length} align="center">
                  No records found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <div className="bm-user-management-table-container">
      <Paper elevation={2} sx={{ padding: "24px", borderRadius: "12px" }}>
        <Box className="management-controls-header">
          {(selectedZonalHead || selectedBranchManager || selectedTeamLead) ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FiArrowLeft />}
                onClick={handleBack}
              >
                Back
              </Button>
              <Typography variant="h5" component="h1">
                {getCurrentTitle()}
              </Typography>
            </Box>
          ) : (
            <Typography variant="h5" component="h1">
              {getCurrentTitle()}
            </Typography>
          )}

          <Box className="filters-wrapper">
            <TextField
              variant="outlined"
              size="small"
              placeholder={`Search by ${getCurrentSearchPlaceholder()} Name...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FiSearch />
                  </InputAdornment>
                ),
              }}
            />
            {!selectedZonalHead && !selectedBranchManager && !selectedTeamLead && (
              <>
                <TextField
                  label="From"
                  type="date"
                  size="small"
                  sx={{ width: 180 }}
                  value={formatDateForInput(dateRange.startDate)}
                  onChange={(e) =>
                    setDateRange((prev) => ({
                      ...prev,
                      startDate: new Date(e.target.value + "T00:00:00"),
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="To"
                  type="date"
                  size="small"
                  sx={{ width: 180 }}
                  value={formatDateForInput(dateRange.endDate)}
                  onChange={(e) =>
                    setDateRange((prev) => ({
                      ...prev,
                      endDate: new Date(e.target.value + "T00:00:00"),
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}
          </Box>
        </Box>

        {!selectedZonalHead && !selectedBranchManager && !selectedTeamLead && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
            <Typography variant="h5">Director Overview</Typography>
            {isDirectOverviewLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                {renderDirectTable("Zonal Head Overview", filteredUsers, "Zonal Head Name")}
                {renderDirectTable("Branch Manager Overview (Direct)", directBranchManagers, "Branch Manager Name")}
                {renderDirectTable("Team Lead Overview (Direct)", directTeamLeads, "Team Lead Name")}
                {renderDirectTable("Relationship Manager Overview (Direct)", directRMs, "Relationship Manager Name")}
              </>
            )}
          </Box>
        )}

        {isLoading ? (
          <Box display="flex" justifyContent="center" p={5}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : selectedZonalHead || selectedBranchManager || selectedTeamLead ? (
          <TableContainer sx={{ mt: 2 }}>
            <Table stickyHeader>
              {selectedTeamLead ? (
                // LEVEL 4: RELATIONSHIP MANAGERS TABLE
                <>
                  <TableHead>
                    <TableRow>
                      <TableCell>RM Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Team Lead</TableCell>
                      <TableCell>Branch Manager</TableCell>
                      <TableCell>Zonal Head</TableCell>
                      <TableCell>Director</TableCell>
                      <TableCell align="right">Calls</TableCell>
                      <TableCell align="right">F2F</TableCell>
                      <TableCell align="right">Appt. Made</TableCell>
                      <TableCell align="right">Appt. Kept</TableCell>
                      <TableCell align="right">Fact Find</TableCell>
                      <TableCell align="right">Reviews</TableCell>
                      <TableCell align="right">Social</TableCell>
                      <TableCell align="right">HIW</TableCell>
                      <TableCell align="right">Referrals</TableCell>
                      <TableCell align="right">Closing Int.</TableCell>
                      <TableCell align="right">Sales Close</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.map((rm) => (
                      <TableRow key={rm.userId} hover>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {rm.name}
                        </TableCell>
                        <TableCell>
                          {rm.leaveStatus ? (
                            <Tooltip
                              title={`On leave until ${formatNotificationDate(
                                rm.leaveEndDate
                              )}`}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  color: "#D97706",
                                  fontWeight: "bold",
                                }}
                              >
                                <FiClock size={14} style={{ marginRight: 4 }} />
                                On Leave
                              </Box>
                            </Tooltip>
                          ) : (
                            <Box sx={{ color: "#16A34A", fontWeight: "bold" }}>
                              Active
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>{selectedTeamLead.name}</TableCell>
                        <TableCell>{selectedBranchManager?.name || "N/A"}</TableCell>
                        <TableCell>{selectedZonalHead?.name || "N/A"}</TableCell>
                        <TableCell>{rm.directorName || "N/A"}</TableCell>
                        <TableCell align="right">
                          {getMetric(rm, "telephoneSalesCalls")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(rm, "faceToFace")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(rm, "appointmentMade")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(rm, "appointmentKept")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(rm, "factFind")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(rm, "serviceReviews")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(rm, "socialCalls")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(rm, "highIncomeWealthy")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(rm, "referrals")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(rm, "closingInterview")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(rm, "salesClose")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </>
              ) : selectedBranchManager ? (
                // LEVEL 3: TEAM LEADS TABLE
                <>
                  <TableHead>
                    <TableRow>
                      <TableCell>Team Lead Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Branch Manager</TableCell>
                      <TableCell>Zonal Head</TableCell>
                      <TableCell>Director</TableCell>
                      <TableCell align="right">Calls</TableCell>
                      <TableCell align="right">F2F</TableCell>
                      <TableCell align="right">Appt. Made</TableCell>
                      <TableCell align="right">Appt. Kept</TableCell>
                      <TableCell align="right">Fact Find</TableCell>
                      <TableCell align="right">Reviews</TableCell>
                      <TableCell align="right">Social</TableCell>
                      <TableCell align="right">HIW</TableCell>
                      <TableCell align="right">Referrals</TableCell>
                      <TableCell align="right">Closing Int.</TableCell>
                      <TableCell align="right">Sales Close</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.map((tl) => (
                      <TableRow
                        key={tl.userId}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleTeamLeadClick(tl)}
                      >
                        <TableCell sx={{ fontWeight: 600, color: "var(--primary-purple)" }}>
                          {tl.name}
                        </TableCell>
                        <TableCell>
                          {tl.leaveStatus ? (
                            <Tooltip
                              title={`On leave until ${formatNotificationDate(
                                tl.leaveEndDate
                              )}`}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  color: "#D97706",
                                  fontWeight: "bold",
                                }}
                              >
                                <FiClock size={14} style={{ marginRight: 4 }} />
                                On Leave
                              </Box>
                            </Tooltip>
                          ) : (
                            <Box sx={{ color: "#16A34A", fontWeight: "bold" }}>
                              Active
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>{selectedBranchManager.name}</TableCell>
                        <TableCell>{selectedZonalHead?.name || "N/A"}</TableCell>
                        <TableCell>{tl.directorName || "N/A"}</TableCell>
                        <TableCell align="right">
                          {getMetric(tl, "telephoneSalesCalls")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(tl, "faceToFace")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(tl, "appointmentMade")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(tl, "appointmentKept")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(tl, "factFind")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(tl, "serviceReviews")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(tl, "socialCalls")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(tl, "highIncomeWealthy")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(tl, "referrals")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(tl, "closingInterview")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(tl, "salesClose")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </>
              ) : selectedZonalHead ? (
                // LEVEL 2: BRANCH MANAGERS TABLE
                <>
                  <TableHead>
                    <TableRow>
                      <TableCell>Branch Manager Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Zonal Head</TableCell>
                      <TableCell>Director</TableCell>
                      <TableCell align="right">Calls</TableCell>
                      <TableCell align="right">F2F</TableCell>
                      <TableCell align="right">Appt. Made</TableCell>
                      <TableCell align="right">Appt. Kept</TableCell>
                      <TableCell align="right">Fact Find</TableCell>
                      <TableCell align="right">Reviews</TableCell>
                      <TableCell align="right">Social</TableCell>
                      <TableCell align="right">HIW</TableCell>
                      <TableCell align="right">Referrals</TableCell>
                      <TableCell align="right">Closing Int.</TableCell>
                      <TableCell align="right">Sales Close</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.map((bm) => (
                      <TableRow
                        key={bm.userId}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleBranchManagerClick(bm)}
                      >
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "var(--primary-purple)",
                          }}
                        >
                          {bm.name}
                        </TableCell>
                        <TableCell>
                          {bm.leaveStatus ? (
                            <Tooltip
                              title={`On leave until ${formatNotificationDate(
                                bm.leaveEndDate
                              )}`}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  color: "#D97706",
                                  fontWeight: "bold",
                                }}
                              >
                                <FiClock size={14} style={{ marginRight: 4 }} />
                                On Leave
                              </Box>
                            </Tooltip>
                          ) : (
                            <Box sx={{ color: "#16A34A", fontWeight: "bold" }}>
                              Active
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>{selectedZonalHead.name}</TableCell>
                        <TableCell>{bm.directorName || "N/A"}</TableCell>
                        <TableCell align="right">
                          {getMetric(bm, "telephoneSalesCalls")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(bm, "faceToFace")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(bm, "appointmentMade")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(bm, "appointmentKept")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(bm, "factFind")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(bm, "serviceReviews")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(bm, "socialCalls")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(bm, "highIncomeWealthy")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(bm, "referrals")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(bm, "closingInterview")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(bm, "salesClose")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </>
              ) : (
                // LEVEL 1: ZONAL HEADS TABLE
                <>
                  <TableHead>
                    <TableRow>
                      <TableCell>Zonal Head Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Director</TableCell>
                      <TableCell align="right">Calls</TableCell>
                      <TableCell align="right">F2F</TableCell>
                      <TableCell align="right">Appt. Made</TableCell>
                      <TableCell align="right">Appt. Kept</TableCell>
                      <TableCell align="right">Fact Find</TableCell>
                      <TableCell align="right">Reviews</TableCell>
                      <TableCell align="right">Social</TableCell>
                      <TableCell align="right">HIW</TableCell>
                      <TableCell align="right">Referrals</TableCell>
                      <TableCell align="right">Closing Int.</TableCell>
                      <TableCell align="right">Sales Close</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.map((zh) => (
                      <TableRow
                        key={zh.userId}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleZonalHeadClick(zh)}
                      >
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "var(--primary-purple)",
                          }}
                        >
                          {zh.name}
                        </TableCell>
                        <TableCell>
                          {zh.leaveStatus ? (
                            <Tooltip
                              title={`On leave until ${formatNotificationDate(
                                zh.leaveEndDate
                              )}`}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  color: "#D97706",
                                  fontWeight: "bold",
                                }}
                              >
                                <FiClock size={14} style={{ marginRight: 4 }} />
                                On Leave
                              </Box>
                            </Tooltip>
                          ) : (
                            <Box sx={{ color: "#16A34A", fontWeight: "bold" }}>
                              Active
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>{zh.directorName || "N/A"}</TableCell>
                        <TableCell align="right">
                          {getMetric(zh, "telephoneSalesCalls")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(zh, "faceToFace")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(zh, "appointmentMade")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(zh, "appointmentKept")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(zh, "factFind")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(zh, "serviceReviews")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(zh, "socialCalls")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(zh, "highIncomeWealthy")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(zh, "referrals")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(zh, "closingInterview")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(zh, "salesClose")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </>
              )}
            </Table>
          </TableContainer>
        ) : null}
      </Paper>

      <Dialog
        open={rmModalOpen}
        onClose={() => setRmModalOpen(false)}
        maxWidth="lg"
        fullWidth
        sx={{ zIndex: 1400 }}
      >
        <DialogTitle>{rmModalTitle || "Relationship Managers"}</DialogTitle>
        <DialogContent dividers>
          {isRmModalLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>
                    Relationship Manager Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Director</TableCell>
                  {metricColumns.map((col) => (
                    <TableCell
                      key={`rm-modal-${col.key}`}
                      align="right"
                      sx={{ fontWeight: 700 }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(rmModalData || []).map((rm) => (
                  <TableRow key={`rm-modal-row-${rm.userId || rm.id}`}>
                    <TableCell sx={{ fontWeight: 700, color: "#5b21b6" }}>
                      {rm.name || rm.username}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: rm.leaveStatus ? "#b45309" : "#16A34A",
                        fontWeight: 700,
                      }}
                    >
                      {rm.leaveStatus ? "On Leave" : "Active"}
                    </TableCell>
                    <TableCell>
                      {rm.director || rm.directorName || rm.director_name || "Director"}
                    </TableCell>
                    {metricColumns.map((col) => (
                      <TableCell
                        key={`rm-modal-${rm.userId}-${col.key}`}
                        align="right"
                      >
                        {getMetric(rm, col.key)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {(rmModalData || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3 + metricColumns.length} align="center">
                      No relationship managers found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setRmModalOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>

      <Dialog
        open={isZonalDialogOpen}
        onClose={closeZonalDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {zonalDetailHead
            ? `${zonalDetailHead.name || zonalDetailHead.username} - Overview`
            : "Zonal Head Overview"}
        </DialogTitle>
        <DialogContent dividers>
          {isZonalDetailLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {renderDirectTable(
                "Branch Managers under Zonal",
                zonalDetailData.branchManagers,
                "Branch Manager Name"
              )}
              {renderDirectTable(
                "Team Leads under Zonal",
                zonalDetailData.teamLeads,
                "Team Lead Name"
              )}
              {renderDirectTable(
                "Relationship Managers under Zonal",
                zonalDetailData.relationshipManagers,
                "Relationship Manager Name"
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeZonalDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bmModalOpen}
        onClose={() => setBmModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {bmModalTitle} -{" "}
          {bmModalMode === "team_leads" ? "Team Leads" : "Relationship Managers"}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Button
              variant={bmModalMode === "team_leads" ? "contained" : "outlined"}
              size="small"
              onClick={() => setBmModalMode("team_leads")}
              disabled={isBmModalLoading}
            >
              View Team Leads
            </Button>
            <Button
              variant={bmModalMode === "rms" ? "contained" : "outlined"}
              size="small"
              onClick={() => setBmModalMode("rms")}
              disabled={isBmModalLoading}
            >
              View Relationship Managers
            </Button>
          </Box>
          {isBmModalLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {bmModalMode === "team_leads"
                      ? "Team Lead Name"
                      : "Relationship Manager Name"}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Director</TableCell>
                  {metricColumns.map((col) => (
                    <TableCell
                      key={`bm-modal-${col.key}`}
                      align="right"
                      sx={{ fontWeight: 700 }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(bmModalData || []).map((row) => (
                  <TableRow
                    key={`bm-modal-row-${row.userId || row.id}`}
                    hover
                    sx={{
                      cursor:
                        bmModalMode === "team_leads" ? "pointer" : "default",
                    }}
                    onClick={async () => {
                      if (bmModalMode !== "team_leads") return;
                      const tlKey =
                        row.id ||
                        row.teamLeadId ||
                        row.team_lead_id ||
                        row.userId;
                      setIsRmModalLoading(true);
                      setRmModalTitle(
                        `RMs under ${row.name || row.username || "Team Lead"}`
                      );
                      setRmModalData([]);
                      setRmModalOpen(true);
                      try {
                        const res = await api.get(
                          `/api/users/relationship-managers-by-director?teamLeadId=${tlKey}`
                        );
                        const rows = res.data || [];
                        const filtered = rows.filter((rm) => {
                          const role =
                            (rm.reportsToRole ||
                              rm.reports_to_role ||
                              rm.reportsToRole) || "";
                          const reportsId =
                            rm.reportsToId ||
                            rm.reports_to_id ||
                            rm.reportsTo ||
                            rm.teamLeadId ||
                            rm.team_lead_id;
                          return (
                            role === "team_lead" &&
                            reportsId !== undefined &&
                            Number(reportsId) === Number(tlKey)
                          );
                        });
                        setRmModalData(filtered);
                      } catch (err) {
                        console.error("Failed to load RMs for TL in BM modal", err);
                      } finally {
                        setIsRmModalLoading(false);
                      }
                    }}
                  >
                    <TableCell sx={{ fontWeight: 700, color: "#5b21b6" }}>
                      {row.name || row.username}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: row.leaveStatus ? "#b45309" : "#16A34A",
                        fontWeight: 700,
                      }}
                    >
                      {row.leaveStatus ? "On Leave" : "Active"}
                    </TableCell>
                    <TableCell>
                      {row.director || row.directorName || row.director_name || "Director"}
                    </TableCell>
                    {metricColumns.map((col) => (
                      <TableCell key={`bm-modal-${row.userId}-${col.key}`} align="right">
                        {getMetric(row, col.key)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {(bmModalData || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3 + metricColumns.length} align="center">
                      No records found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBmModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DirectorUserManagement;
